import useApiClient from "hooks/useApiClient";
import { useState } from "react";

import { useTranslation } from "react-i18next";
import { useFlightContext } from "./useFlightContext";
import useFlightSearchParams from "./useFlightSearchParams";
import useMimir from "hooks/useMimir";

const useGetFlightAvailbility = () => {
    const { api, urls } = useApiClient();
    const {
        setAvailabilityResults,
        setAvailabilityLoading,
        setAvailabilityError,
    } = useFlightContext();

    const [error, setError] = useState(null);
    const { i18n } = useTranslation();
    const { prepareAvailabilityPayload } = useFlightSearchParams();
    const { mimir } = useMimir()

    const parseAvailabilityResponse = (data, tripType) => {
        const response = data?.result;
        const airlineDictionary = response["airlineDictionary"];
        const airportDictionary = response["airportDictionary"];
        const groupOfFlights = response["groupOfFlights"];
        const isDomesticFlight = response["isDomesticFlight"];
        const currency = response["currency"];
        const recommendations = response["recommendations"].map((el) => {
            el.flights.forEach((f, i) => {
                el.flights[i] = f.map((fl) => groupOfFlights["bounds"][i][fl]);
            });
            //el.subRecommendations = el.subRecommendations.filter((sub) => sub);
            return el;
        });
        let allFlightCombinations = response["allFlightCombinations"];
        allFlightCombinations.forEach((item) => {
            item.bounds = item.bounds.map(
                (bound, index) => groupOfFlights["bounds"][index][bound.id]
            );
            item.recommendation = recommendations[item.recommendationIndex];
            let isZoneB = false;
            item.provider = data["provider"];
            !["1A", "1T"].includes(data.provider) && (isZoneB = true);
            item.isZoneB = isZoneB;
            item.provider_id = data["provider_id"];
            item.isDomesticFlight = isDomesticFlight;
            item.tripType = tripType;
            item.flightNeedExchangeDocuments =
                response["flightNeedExchangeDocuments"] || false;
        });
        allFlightCombinations = allFlightCombinations.filter(
            (item) => item.recommendation
        );
        // Sort flights by elapsedFlyingTime
        allFlightCombinations
            .map((item) => item.recommendation.flights)
            .flat()
            .forEach((flight) => {
                flight.sort((a, b) =>
                    a.elapsedFlyingTime.localeCompare(b.elapsedFlyingTime)
                );
            });

        return {
            airlineDictionary,
            airportDictionary,
            // groupOfFlights,
            // recommendations,
            allFlightCombinations,
            isDomesticFlight,
            currency,
        };
    };

    const getParsedAvailabilityResponse = async (payload) => {
        try {
            const queryParams = new URLSearchParams(payload).toString();
            const requestUrl = `${urls.flight.availability}?${queryParams}`;
            const res = await api.get(requestUrl);
            if (
                res.status === 204 ||
                res.status === 500 ||
                res?.data?.result?.error ||
                res?.data?.result?.status === "ERR"
            ) {
                return null;
            }
            const parsedFlights = parseAvailabilityResponse(
                res?.data,
                payload["tripType"]
            );
            return parsedFlights;
        } catch (error) {
            console.error("Error fetching availability:", error);
            return null;
        }
    };

    const getProviders = async (overridePayload) => {
        setAvailabilityLoading(true);
        setAvailabilityError(null);
        setAvailabilityResults(null);
        const AvailabilityPayload = overridePayload || prepareAvailabilityPayload();
        let providersNumber = 0;
        let failedProviders = 0;
        let mimirData = {
            type: "FLIGHT",
            event: "AVAILABILITY",
            param1: JSON.stringify(AvailabilityPayload || {}),
            param2: [],
            param3: [],
            param4: `${AvailabilityPayload.origin_1},${AvailabilityPayload.destination_1},${AvailabilityPayload.tripType}` 
        };
        try {
            const res = await api.get(urls.flight.providers);
            if (res.status === 200) {
                providersNumber = res.data.length;
                const payloads = res.data.map((provider) => ({
                    ...AvailabilityPayload,
                    provider_id: provider.id,
                    provider_code: provider.code,
                    language: i18n.language,
                }));
                await Promise.all(
                    [...payloads].map(async (payload) => {
                        const result = await getParsedAvailabilityResponse(
                            payload
                        );
                        if (result) {
                            setAvailabilityResults((prevResults) => {
                                const updatedResults = {
                                    airlineDictionary: {
                                        ...prevResults?.airlineDictionary,
                                        ...result.airlineDictionary,
                                    },
                                    airportDictionary: {
                                        ...prevResults?.airportDictionary,
                                        ...result.airportDictionary,
                                    },
                                    //   recommendations: [
                                    //     ...(prevResults?.recommendations || []),
                                    //     ...result.recommendations,
                                    //   ],
                                    allFlightCombinations: [
                                        ...(prevResults?.allFlightCombinations ||
                                            []),
                                        ...result.allFlightCombinations,
                                    ],
                                    currency:
                                        result.currency ||
                                        prevResults?.currency,
                                    isDomesticFlight: result.isDomesticFlight,
                                };
                                return updatedResults;
                            });
                            setAvailabilityLoading(false);
                            mimirData["param2"].push(`${payload?.provider_code}:${result?.allFlightCombinations?.length}`)
                        } else {
                            failedProviders++;
                            mimirData["param3"].push(`${payload?.provider_code}`)
                            console.log(
                                "No valid results for this provider " +
                                    payload["provider_id"]
                            );
                        }
                    })
                ).finally(() => {
                    mimirData["param2"] = JSON.stringify(mimirData["param2"]);
                    mimirData["param3"] = JSON.stringify(mimirData["param3"]);
                    mimir.log(mimirData);
                });
                if (failedProviders === providersNumber) {
                    setAvailabilityError(
                        "No flights available, please try again later."
                    );
                    setAvailabilityResults(null);
                }
            } else {
                setAvailabilityError(
                    "An error has occurred, please try again later."
                );
                setAvailabilityResults(null);
            }
        } catch (err) {
            console.error("Error fetching providers:", err);
            setAvailabilityError(
                "An error has occurred, please try again later."
            );
            setAvailabilityResults(null);
        } finally {
            setAvailabilityLoading(false);
        }
    };

    return { error, setError, getProviders };
};

export default useGetFlightAvailbility;
